/**
 * Die Bausteine der Oberflaeche (CLAUDE.md §7).
 *
 * Alles hier ist auf eine Hand mit Arbeitshandschuhen ausgelegt: Touch-Ziele
 * mindestens 48 px, hoher Kontrast, Dunkelmodus. Sonne und Nacht.
 */

import type { ReactNode } from "react";
import type { Verbleib } from "@thw/contracts";
import { useStatusArten } from "../lokal/bestand.js";
import { VERBLEIB_TEXT } from "../lokal/beschreiben.js";

export { VERBLEIB_TEXT } from "../lokal/beschreiben.js";

/**
 * Der Farbpunkt am Artikel -- Name und Farbe, wie der Ortsverband sie
 * eingerichtet hat (ADR 0005).
 *
 * Farbe **und** Text: Rund jeder zwoelfte Mann sieht Rot und Gruen nicht
 * auseinander, und an der Einsatzstelle ist niemand danach gefragt worden.
 */
export function StatusPunkt({ status }: { status: string }) {
  const art = useStatusArten().find((a) => a.schluessel === status);
  return (
    <span className="inline-flex items-center gap-2">
      <Farbpunkt farbe={art?.farbe ?? "#a3a3a3"} />
      <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
        {art?.bezeichnung ?? status}
      </span>
    </span>
  );
}

/** Ein Farbpunkt mit hellem Rand -- auf dunklem Grund genauso sichtbar. */
export function Farbpunkt({ farbe, gross = false }: { farbe: string; gross?: boolean }) {
  return (
    <span
      className={`inline-block shrink-0 rounded-full ring-2 ring-white/70 dark:ring-black/40 ${gross ? "size-5" : "size-3"}`}
      style={{ backgroundColor: farbe }}
      aria-hidden
    />
  );
}

/** Farben des Verbleibs nach KONZEPT.md §7.2: Einsatz blau, Ausleihe gelb. */
const VERBLEIB_KLASSE: Record<Verbleib, string> = {
  nicht_erfasst: "bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100",
  im_lager: "bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100",
  im_einsatz: "bg-sky-600 text-white",
  ausgeliehen: "bg-amber-400 text-neutral-900",
};

/** Der Verbleib als kleines Schild. */
export function VerbleibSchild({ verbleib }: { verbleib: Verbleib }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${VERBLEIB_KLASSE[verbleib]}`}
    >
      {VERBLEIB_TEXT[verbleib]}
    </span>
  );
}

export interface KnopfEigenschaften {
  children: ReactNode;
  onClick?: () => void;
  art?: "haupt" | "neben" | "still";
  type?: "button" | "submit";
  disabled?: boolean;
}

/** Ein Knopf, der sich mit Handschuhen treffen laesst. */
export function Knopf({
  children,
  onClick,
  art = "neben",
  type = "button",
  disabled = false,
}: KnopfEigenschaften) {
  const klassen = {
    haupt: "bg-blue-700 text-white active:bg-blue-800",
    neben:
      "bg-neutral-200 text-neutral-900 active:bg-neutral-300 dark:bg-neutral-700 dark:text-white",
    still: "bg-transparent text-neutral-600 underline dark:text-neutral-300",
  }[art];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`min-h-12 rounded-xl px-5 text-base font-semibold disabled:opacity-40 ${klassen}`}
    >
      {children}
    </button>
  );
}

export type MeldungsArt = "gut" | "warnung" | "still";

/**
 * Die Rueckmeldung nach einem Scan.
 *
 * Gross, farbig und ganz oben -- man soll sie im Augenwinkel sehen, waehrend
 * man den naechsten Artikel greift.
 */
export function Rueckmeldung({
  art,
  kopf,
  zusatz,
  aktion,
}: {
  art: MeldungsArt;
  kopf: string;
  zusatz?: ReactNode;
  /** Ein Knopf in der Meldung, etwa „Rückgängig“ (ADR 0013 §15). */
  aktion?: { text: string; tun: () => void };
}) {
  const klassen = {
    gut: "bg-emerald-600 text-white",
    warnung: "bg-amber-400 text-neutral-900",
    still: "bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100",
  }[art];

  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-xl p-4 ${klassen}`}
      role="status"
      aria-live="polite"
    >
      <div className="min-w-0">
        <p className="text-lg font-bold">{kopf}</p>
        {zusatz !== undefined && <div className="mt-1 text-sm opacity-90">{zusatz}</div>}
      </div>
      {aktion !== undefined && (
        <button
          type="button"
          onClick={aktion.tun}
          className="min-h-12 shrink-0 rounded-xl border-2 border-current px-4 font-semibold active:opacity-70"
        >
          {aktion.text}
        </button>
      )}
    </div>
  );
}

/** Inventarnummer in gleicher Zeichenbreite -- Ziffern stehen untereinander. */
export function Nummer({ children }: { children: ReactNode }) {
  return <span className="font-mono text-sm tracking-tight">{children}</span>;
}

/** Eine Karte mit Ueberschrift -- die Grundform der Verwaltungsseiten. */
export function Karte({ titel, children }: { titel?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl bg-white p-4 dark:bg-neutral-800">
      {titel !== undefined && <h2 className="text-lg font-bold">{titel}</h2>}
      {children}
    </section>
  );
}

const EINGABE_KLASSE =
  "min-h-12 rounded-xl border-2 border-neutral-300 bg-white px-4 text-base dark:border-neutral-600 dark:bg-neutral-900 dark:text-white";

/** Ein beschriftetes Eingabefeld. */
export function Eingabe({
  beschriftung,
  wert,
  aendern,
  art = "text",
  hinweis,
  autoComplete = "off",
}: {
  beschriftung: string;
  wert: string;
  aendern: (neu: string) => void;
  art?: "text" | "password";
  hinweis?: string | undefined;
  autoComplete?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-semibold">{beschriftung}</span>
      <input
        type={art}
        className={EINGABE_KLASSE}
        value={wert}
        onChange={(e) => {
          aendern(e.target.value);
        }}
        autoComplete={autoComplete}
        autoCapitalize="none"
      />
      {hinweis !== undefined && (
        <span className="text-sm text-neutral-500 dark:text-neutral-400">{hinweis}</span>
      )}
    </label>
  );
}

/** Ein Schalter fuer Ja/Nein -- gross genug fuer Handschuhe. */
export function Schalter({
  beschriftung,
  an,
  aendern,
  hinweis,
  gesperrt = false,
}: {
  beschriftung: string;
  an: boolean;
  aendern: (neu: boolean) => void;
  hinweis?: string | undefined;
  gesperrt?: boolean;
}) {
  return (
    <label
      className={`flex min-h-12 items-center justify-between gap-4 ${gesperrt ? "opacity-50" : ""}`}
    >
      <span className="flex flex-col">
        <span className="font-semibold">{beschriftung}</span>
        {hinweis !== undefined && (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">{hinweis}</span>
        )}
      </span>
      <input
        type="checkbox"
        role="switch"
        className="size-7 shrink-0 accent-blue-700"
        checked={an}
        disabled={gesperrt}
        onChange={(e) => {
          aendern(e.target.checked);
        }}
      />
    </label>
  );
}

/** Auswahl aus wenigen Moeglichkeiten als grosse Knoepfe nebeneinander. */
export function Wahl<T extends string>({
  beschriftung,
  wert,
  moeglichkeiten,
  aendern,
}: {
  beschriftung: string;
  wert: T;
  moeglichkeiten: readonly { wert: T; text: string }[];
  aendern: (neu: T) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-1 text-sm font-semibold">{beschriftung}</legend>
      <div className="flex gap-1 rounded-xl bg-neutral-100 p-1 dark:bg-neutral-900">
        {moeglichkeiten.map((m) => (
          <button
            key={m.wert}
            type="button"
            aria-pressed={m.wert === wert}
            className={`min-h-12 flex-1 rounded-lg px-3 text-sm font-semibold ${
              m.wert === wert
                ? "bg-blue-700 text-white"
                : "text-neutral-700 active:bg-neutral-200 dark:text-neutral-200 dark:active:bg-neutral-700"
            }`}
            onClick={() => {
              aendern(m.wert);
            }}
          >
            {m.text}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Hinweis, dass etwas nur mit Netz geht (ADR 0006).
 *
 * Nicht versteckt und nicht als Fehler: Die Verwaltung braucht Netz, das ist
 * so gewollt. Wer es liest, weiss, dass er spaeter wiederkommen muss.
 */
export function NurMitNetz() {
  return (
    <Rueckmeldung
      art="still"
      kopf="Ohne Netz nur ansehen"
      zusatz="Benutzer und Stammdaten lassen sich nur mit Verbindung zum Server ändern. Buchen geht weiterhin."
    />
  );
}

export function Ladeanzeige({ was }: { was: string }) {
  return <p className="p-6 text-center text-neutral-500">{was} wird geladen …</p>;
}

export function Leer({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-neutral-500 dark:border-neutral-600">
      {children}
    </p>
  );
}
